import '../styles/globals.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type Datastore } from '@youversion/analytics-types';
import { PagesRouterAnalytics } from '@youversion/analytics-youversion-nextjs';
import type { AppProps } from 'next/app';
import { appWithTranslation, i18n } from 'next-i18next';
import { setCookie } from 'nookies';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';

import { API_ENV, COMMIT_SHA, DATAMAN_URL, LOGIN_CLIENT_ID } from '@/env';

const analyticsConfig: Datastore.Config = {
  allowExternalInstallId: false,
  applicationId: 'com.youversion.login',
  appVersion: COMMIT_SHA,
  baseDomain: 'youversion.com',
  clientId: LOGIN_CLIENT_ID,
  debug: false,
  endpoint: DATAMAN_URL,
  environment: API_ENV ?? 'development',
  localeFallback: i18n?.language ?? 'en-US',
  relatedDomains: ['youversion.com', 'bible.com', 'thewardro.be'],
};

function MyApp({ Component, pageProps }: AppProps) {
  const dir = i18n?.dir() ?? 'ltr';
  const language = i18n?.resolvedLanguage;

  useEffect(() => {
    document.querySelector('html')?.setAttribute('dir', dir);
  }, [dir]);

  useEffect(() => {
    if (language) {
      setCookie(null, 'locale', language);
    }
  }, [language]);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  return (
    <>
      <PagesRouterAnalytics config={analyticsConfig} />
      <QueryClientProvider client={queryClient}>
        <Toaster position='top-right' toastOptions={{ duration: Infinity }} />
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <Component {...pageProps} />
      </QueryClientProvider>
    </>
  );
}

export default appWithTranslation(MyApp);
